/**
 * Vincent #266 — Panneau "Termes du dictionnaire détectés dans cette vidéo".
 * Extrait de VideoDetailPage.js (refactor 2026-06-12) — zéro changement de logique.
 * Lazy : ne fetch qu'à la première expansion. Affiche la liste des termes du
 * dictionnaire qui apparaissent dans le transcript, avec compte d'occurrences.
 * Clique → ouvre la page dictionnaire sur le terme.
 *
 * Vincent bogue 676 suite (Eric 2026-02-XX) — Bouton « Régénérer le dictionnaire »
 * pour les éditeurs (canEdit). Appelle POST /api/videos/{id}/generate-dictionary
 * qui lance le pipeline dédié (1 seul appel GPT-5.2, upsert-on-exists : enrichit
 * les termes déjà connus au lieu de créer des doublons).
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "../../contexts/LanguageContext";
import { API_BASE as API } from "../../lib/apiBase";
import { confirmDialog } from "../../lib/uiDialogs";
import { ArrowClockwise, SpinnerGap } from "@phosphor-icons/react";

export default function JargonTermsPanel({ videoId, canEdit = false }) {
  const { lang } = useLang();
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  if (!videoId) return null;

  const fetchTerms = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/videos/${videoId}/jargon-terms`, { withCredentials: true });
      setTerms(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === "fr" ? "Erreur" : "Error"));
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    const ok = await confirmDialog(
      lang === "fr"
        ? "Régénérer les termes du dictionnaire pour cette vidéo ?\n\nGPT-5.2 va extraire les termes techniques du transcript.\n\n  • Les termes DÉJÀ dans le dictionnaire seront ENRICHIS (définition fusionnée, jamais dupliquée).\n  • Les nouveaux termes seront créés.\n\nCoût : 1 appel GPT-5.2 (~30 s à 2 min) facturé sur ta clé BYOK OpenAI.\n\nContinuer ?"
        : "Regenerate dictionary terms for this video?\n\nGPT-5.2 will extract technical terms.\n\n  • Terms ALREADY in the dictionary will be ENRICHED (definitions merged, never duplicated).\n  • New terms will be created.\n\nCost: 1 GPT-5.2 call (~30 s to 2 min) billed to your BYOK OpenAI key.",
      { confirmLabel: lang === "fr" ? "Régénérer" : "Regenerate", tone: "warning" },
    );
    if (!ok) return;
    setRegenerating(true);
    const toastId = toast.loading(
      lang === "fr" ? "📚 Extraction des termes du dictionnaire…" : "📚 Extracting dictionary terms…",
      { duration: Infinity },
    );
    try {
      const { data } = await axios.post(
        `${API}/api/videos/${videoId}/generate-dictionary`,
        {},
        { withCredentials: true, timeout: 5 * 60 * 1000 },
      );
      const created = data.terms_created || 0;
      const enriched = data.terms_enriched || 0;
      const skipped = data.terms_skipped || 0;
      toast.success(
        lang === "fr"
          ? `📚 Dictionnaire mis à jour : ${created} nouveau(x), ${enriched} enrichi(s), ${skipped} inchangé(s).`
          : `📚 Dictionary updated: ${created} new, ${enriched} enriched, ${skipped} unchanged.`,
        { id: toastId, duration: 8000 },
      );
      // Rafraîchit la liste des termes détectés (l'endpoint /jargon-terms
      // scanne à nouveau le transcript contre la nouvelle version du dico)
      setTerms(null);
      await fetchTerms();
    } catch (err) {
      toast.error(
        (lang === "fr" ? "❌ Échec : " : "❌ Failed: ") +
          (err.response?.data?.detail || err.message),
        { id: toastId, duration: 10000 },
      );
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="bg-white border border-blue-100 rounded-lg mb-4 overflow-hidden" data-testid="jargon-terms-panel">
      <button
        onClick={async () => {
          const next = !expanded;
          setExpanded(next);
          if (next && !terms && !loading) {
            await fetchTerms();
          }
        }}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-blue-50/40 transition-colors text-left"
        data-testid="jargon-terms-toggle"
      >
        <div className="flex items-center gap-2 text-sm font-medium text-blue-900">
          📚 {lang === "fr" ? "Termes du dictionnaire détectés dans cette vidéo" : "Dictionary terms detected in this video"}
          {terms && (
            <span className="text-xs font-normal text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
              {terms.total_terms} {lang === "fr" ? "termes" : "terms"} · {terms.total_occurrences} {lang === "fr" ? "occurrences" : "occurrences"}
            </span>
          )}
        </div>
        <span className="text-xs text-blue-600">{expanded ? "▾" : "▸"}</span>
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-blue-50">
          {canEdit && (
            <div className="flex justify-end pt-3">
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded bg-[#002FA7] text-white hover:bg-[#001f70] disabled:opacity-50 shadow-sm"
                data-testid="regenerate-dictionary-btn"
                title={
                  lang === "fr"
                    ? "Relance GPT-5.2 pour extraire les termes du dictionnaire (enrichit les termes existants, crée les nouveaux)"
                    : "Re-run GPT-5.2 to extract dictionary terms (enriches existing, creates new)"
                }
              >
                {regenerating ? (
                  <SpinnerGap size={12} className="animate-spin" />
                ) : (
                  <ArrowClockwise size={12} weight="bold" />
                )}
                {regenerating
                  ? (lang === "fr" ? "En cours…" : "Running…")
                  : (terms && terms.terms_found && terms.terms_found.length > 0
                      ? (lang === "fr" ? "Régénérer le dictionnaire" : "Regenerate dictionary")
                      : (lang === "fr" ? "Générer le dictionnaire" : "Generate dictionary"))}
              </button>
            </div>
          )}
          {loading ? (
            <div className="py-4 text-center text-xs text-zinc-500">
              {lang === "fr" ? "Recherche en cours…" : "Searching…"}
            </div>
          ) : !terms || terms.terms_found.length === 0 ? (
            <div className="py-4 text-center text-xs text-zinc-500" data-testid="jargon-terms-empty">
              {lang === "fr"
                ? "Aucun terme du dictionnaire n'a été détecté dans cette vidéo."
                : "No dictionary terms detected in this video."}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="jargon-terms-list">
              {terms.terms_found.map((t) => (
                <a
                  key={t.jargon_entry_id}
                  /* Bogue 1361 (Eric 2026-06-30) — Deux erreurs dans cette seule
                      ligne : la route `/dashboard/jargon` N'EXISTE PAS (c'est
                      `/dashboard/dictionnaire`, `/dashboard/admin/jargon`
                      étant réservée aux admins), d'où le 404 ; et le
                      surlignage se lit `?focus=`, jamais `?highlight=` —
                      le terme n'aurait donc pas été mis en évidence même en
                      arrivant sur la bonne page. */
                  href={`/dashboard/dictionnaire?focus=${t.jargon_entry_id}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 text-xs"
                  data-testid={`jargon-term-${t.jargon_entry_id}`}
                  title={t.definition_short || ""}
                >
                  <span className="font-medium text-blue-900">{t.term}</span>
                  <span className="text-[10px] text-blue-600 bg-white px-1.5 py-0.5 rounded-full border border-blue-200">×{t.occurrence_count}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
